import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Row, Column } from 'mage-components';
import { translator } from 'mage-i18n';
import { getMerchantAwareTranslator } from '@/selectors/merchant';

import {
  ActionIconCheckmark,
  ActionIconPlus,
  ParagraphTertiary,
  SpacerHorizontal,
  SpacerVertical,
} from '@klarna/bubble-ui';

import { KlarnaLogo } from './KlarnaLogo';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  flex-grow: 1;
`;

const ChevronContainer = styled.div`
  background-color: #ffffff;
  border-bottom: 2px solid #f0f1f1;
  border-radius: 8px 8px 0 0;

  width: 100%;
  padding: 13px 15px;

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  cursor: pointer;
`;

const DetailsContainer = styled.div`
  background-color: #ffffff;
  border-top: none;
  border-radius: 0 0 8px 8px;

  width: 100%;
  padding: 15px 15px 15px 12px;

  height: max-content;

  cursor: auto;
`;

const DetailsFlexbox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
`;

const DetailsItem = styled.div`
  flex: 1;
  height: 60px;

  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
`;

const TitleWithKlarnaLogoContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 4px;
`;

const ProductDetail = ({ tMerchantAware, tPrefix, detail, kind, isLast }) => {
  const body = tMerchantAware(`${tPrefix}.${kind}s.${detail.id}.description`) || '';
  const Icon =
    kind === 'feature' ? (
      <ActionIconPlus colorToken="colors/text/accent" />
    ) : (
      <ActionIconCheckmark colorToken="colors/text/success" />
    );
  const Logo = detail.logo;

  return (
    <Column columns={{ tablet: 6, mobile: 12 }}>
      <DetailsItem>
        <div style={{ flexGrow: 0, flexShrink: 1, flexBasis: '0%' }}>{Icon}</div>

        <SpacerHorizontal extraSmall />

        {/* We need marginTop to align the title with the icon above */}
        <div style={{ flex: 9, marginTop: Logo ? 0 : 4 }}>
          <TitleWithKlarnaLogoContainer>
            {Logo}
            <ParagraphTertiary bold correctFootprint>
              {tMerchantAware(`${tPrefix}.${kind}s.${detail.id}.name`)}
            </ParagraphTertiary>
          </TitleWithKlarnaLogoContainer>
        </div>
      </DetailsItem>

      {body && (
        <DetailsItem>
          <div style={{ flexGrow: 0, flexShrink: 1, flexBasis: '0%', minWidth: 20 }} />

          <SpacerHorizontal extraSmall />

          <div style={{ flex: 9 }}>
            <ParagraphTertiary correctFootprint light>
              {body}
            </ParagraphTertiary>
          </div>
        </DetailsItem>
      )}
      {/* eslint-disable-next-line max-len */}
      {/* body with KlarnaLogo inside breaks the spacing, thus the small/extraSmall work-around */}
      {!isLast && <SpacerVertical small={!Logo || !!body} extraSmall={Logo && !body} />}
    </Column>
  );
};

ProductDetail.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string.isRequired,
    logo: PropTypes.element,
  }).isRequired,
  kind: PropTypes.string.isRequired,
  tMerchantAware: PropTypes.func.isRequired,
  tPrefix: PropTypes.string.isRequired,
  isLast: PropTypes.bool,
};

const ProductCardDetailsExpandedInner = ({ disabled = false, product, t, tMerchantAware }) => {
  const tPrefix = `products-fe.products.${product.id}`;

  const disableClick = event => {
    event.stopPropagation();
    return false;
  };

  const features = {
    kco_one: [
      { id: 'other_payment_options' },
      { id: 'on_site_messaging', logo: <KlarnaLogo type="white" /> },
      { id: 'billing' },
      { id: 'shipping' },
      { id: 'post_purchase' },
    ],
    kp_one: [],
  }[product.id];

  return (
    <Container>
      <ChevronContainer>
        <ParagraphTertiary correctFootprint>
          {t('products-fe.products.why_product', { name: tMerchantAware(`${tPrefix}.name`) })}
        </ParagraphTertiary>
      </ChevronContainer>

      <DetailsContainer onClick={disableClick}>
        <AnimatePresence>
          <motion.div
            key={`product-details-${product.id}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <DetailsFlexbox>
              <SpacerVertical extraSmall />
              <Row>
                {product.options
                  .filter(o => o.isCompatible)
                  .map((o, idx) => (
                    <ProductDetail
                      tPrefix={tPrefix}
                      detail={o}
                      tMerchantAware={tMerchantAware}
                      key={`product-option-${product.id}-${o.id}`}
                      isLast={
                        idx === product.options.length - 1 && (!features || features.length === 0)
                      }
                      kind="option"
                    />
                  ))}
                {features.map((f, idx) => (
                  <ProductDetail
                    tPrefix={tPrefix}
                    detail={f}
                    tMerchantAware={tMerchantAware}
                    key={`product-feature-${product.id}-${f.id}`}
                    isLast={idx === features.length - 1}
                    kind="feature"
                  />
                ))}
              </Row>
            </DetailsFlexbox>
          </motion.div>
        </AnimatePresence>
      </DetailsContainer>
    </Container>
  );
};

ProductCardDetailsExpandedInner.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        isCompatible: PropTypes.bool.isRequired,
      }),
    ).isRequired,
  }).isRequired,
  t: PropTypes.func.isRequired,
  tMerchantAware: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export const ProductCardDetailsExpanded = connect(state => ({
  t: translator(state),
  tMerchantAware: getMerchantAwareTranslator(state),
}))(ProductCardDetailsExpandedInner);
